/**
 * Cash Tracker fields — 16px, normal tracking, no type=number.
 * Global App.css forces number inputs to 11px mono, which stacks digits.
 * Dates are plain text (DD.MM.YYYY), never type=date / calendar UI.
 */
import { ClearableNumberInput } from './ClearableNumberInput';
import { preserveFieldSelection, selectFieldText } from '../utils/selectFieldText';

export const CASH_INPUT_CLASS =
  'box-border block w-full min-w-0 h-11 rounded-md border border-gray-200 bg-white px-3 py-2 text-[16px] leading-normal tracking-normal text-gray-900 placeholder:text-gray-300 shadow-none md:text-[16px]';

/** Dense table-cell control — still text/decimal, never type=number. */
export const CASH_CELL_CLASS =
  'box-border block w-full min-w-0 max-w-full h-8 rounded border border-gray-200 bg-white px-1.5 py-0 text-[12px] leading-tight tracking-normal text-gray-900 placeholder:text-gray-300 shadow-none overflow-hidden text-ellipsis tabular-nums md:text-[12px]';

const SELECT_CHEVRON =
  "appearance-none bg-no-repeat bg-[length:10px_10px] bg-[right_6px_center] bg-[url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M2.5 4.2 6 8.2l3.5-4'/%3E%3C/svg%3E\")]";

export const CASH_CELL_SELECT_CLASS =
  `${CASH_CELL_CLASS} ${SELECT_CHEVRON} pr-6`;

const CASH_FIELD_SELECT_CLASS =
  `${CASH_INPUT_CLASS} ${SELECT_CHEVRON} pr-8`;

function fieldClass(size, { select = false } = {}) {
  if (size === 'cell') return select ? CASH_CELL_SELECT_CLASS : CASH_CELL_CLASS;
  return select ? CASH_FIELD_SELECT_CLASS : CASH_INPUT_CLASS;
}

export function CashTextInput({
  value,
  onChange,
  placeholder,
  id,
  testId,
  type = 'text',
  inputMode,
  list,
  size = 'field',
  name,
  'aria-label': ariaLabel,
  onBlur,
}) {
  return (
    <input
      id={id}
      name={name}
      // Never render a native date picker — cash dates are typed DD.MM.YYYY.
      type={type === 'number' || type === 'date' ? 'text' : type}
      inputMode={inputMode}
      list={list}
      autoComplete="off"
      spellCheck={false}
      value={value ?? ''}
      placeholder={placeholder}
      aria-label={ariaLabel}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      onFocus={selectFieldText}
      onMouseUp={preserveFieldSelection}
      data-testid={testId}
      className={fieldClass(size)}
    />
  );
}

export function CashAmountInput({
  value,
  onChange,
  testId,
  min = 0,
  size = 'field',
  name,
  'aria-label': ariaLabel = 'Amount',
}) {
  return (
    <ClearableNumberInput
      value={value}
      onChange={onChange}
      min={min}
      name={name}
      data-testid={testId}
      aria-label={ariaLabel}
      className={`${fieldClass(size)} text-right`}
    />
  );
}

export function CashSelect({
  value,
  onChange,
  children,
  testId,
  size = 'field',
  name,
  'aria-label': ariaLabel,
}) {
  return (
    <select
      name={name}
      value={value}
      aria-label={ariaLabel}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className={fieldClass(size, { select: true })}
    >
      {children}
    </select>
  );
}
